// src/ViewDatSubmission.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getAuth,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes, uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage';
import {
  getFunctions,
  httpsCallable
} from 'firebase/functions';

const ViewDatSubmission = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const asset    = location.state?.asset || {};

  // Detect "from existing release" flow and memoize the summary map (stable identity)
  const isFromSummary = asset?.mode === 'fromSummary';
  const sourceMap = useMemo(
    () => (isFromSummary ? (asset?.sourceMap ?? null) : null),
    [isFromSummary, asset]
  );

  const [artworkURL, setArtworkURL] = useState(null);
  const [submitting, setSubmitting]  = useState(false);

  // Derive stable inputs for artwork preview
  const fromSummaryArtwork = useMemo(() => {
    if (!sourceMap) return null;
    return sourceMap.ArtworkURL || sourceMap.imageURL || null;
  }, [sourceMap]);

  const formArtworkFile = asset?.artwork || null;

  // Only create/revoke object URLs when the underlying value changes
  useEffect(() => {
    // Summary path: just assign the remote URL string
    if (fromSummaryArtwork) {
      setArtworkURL(fromSummaryArtwork);
      return;
    }

    // Form path: create a blob URL for the File object
    if (formArtworkFile instanceof File) {
      const url = URL.createObjectURL(formArtworkFile);
      setArtworkURL(url);
      return () => URL.revokeObjectURL(url);
    }

    setArtworkURL(null);
  }, [fromSummaryArtwork, formArtworkFile]);

  const isNewArtist = !isFromSummary && asset.hasProfile === 'no';
  const artistInfo  = isFromSummary ? null : (isNewArtist ? asset.newArtist : asset.selectedArtist);

  const cleanUsername = (username = '') =>
    username.startsWith('@') ? username.slice(1) : username;

  const artistUsername = isFromSummary
    ? cleanUsername(sourceMap?.creatorUsername || sourceMap?.username || '')
    : cleanUsername(artistInfo?.username || '');

  const artistName = isFromSummary
    ? (sourceMap?.ArtistName || sourceMap?.artistName || artistUsername || 'Artist')
    : (isNewArtist
        ? (artistInfo?.ArtistName || artistInfo?.username || 'Artist')
        : (artistInfo?.name || artistInfo?.username || 'Artist'));

  const artistDisplay = artistUsername ? `@${artistUsername}` : artistName;

  // Profile image preview (same stability guarantees as artwork)
  const [profilePreviewURL, setProfilePreviewURL] = useState(null);

  const fromSummaryProfile = sourceMap?.profileImageUrl || null;
  const formProfileFile = (!isFromSummary && asset?.hasProfile === 'no')
    ? asset?.newArtist?.profileImageUrl || null
    : null;

  useEffect(() => {
    if (fromSummaryProfile) {
      setProfilePreviewURL(fromSummaryProfile);
      return;
    }
    if (formProfileFile instanceof File) {
      const url = URL.createObjectURL(formProfileFile);
      setProfilePreviewURL(url);
      return () => URL.revokeObjectURL(url);
    }
    setProfilePreviewURL(null);
  }, [fromSummaryProfile, formProfileFile]);

  /* ─── main submit ─── */
  const handleFinalSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const functions = getFunctions();

      if (isFromSummary) {
        // Simple handoff — no uploads. Clone the map in Cloud Function.
        const releaseFromSummary = httpsCallable(functions, 'releaseDATFromSummary');
        const contentId = String(asset.contentId || sourceMap?.contentId || sourceMap?.id || '').trim();
        const tier      = String(asset.tier || asset.searchTier || 'Bronze');
        if (!contentId) {
          throw new Error('Missing contentId for summary release.');
        }
        await releaseFromSummary({ contentId, tier });
        alert('🚀 DAT released from existing release!');
        navigate('/');
        return;
      }

      // ORIGINAL FORM FLOW BELOW

      // Step 1: identify creator
      if (!asset?.selectedArtist?.id) {
        throw new Error('Missing selected artist.');
      }
      const artistId    = asset.selectedArtist.id;
      const creatorName = asset.selectedArtist.name || asset.selectedArtist.username || 'Artist';
      const artistLower = String(creatorName || '').toLowerCase();

      // Step 2: upload files to Firebase Storage
      const storage = getStorage();
      const ts      = Date.now();

      // artwork
      if (!asset.artwork) throw new Error('Please upload artwork.');
      const artworkRef  = ref(storage, `dat_artwork/${ts}_${asset.artwork.name}`);
      const artworkTask = uploadBytesResumable(artworkRef, asset.artwork);
      await new Promise((res, rej) =>
        artworkTask.on('state_changed', () => {}, err => rej(err), () => res())
      );
      const artworkURLRemote = await getDownloadURL(artworkTask.snapshot.ref);

      // audio
      if (!asset.songFile) throw new Error('Please upload audio.');
      const songRef  = ref(storage, `dat_audio/${ts}_${asset.songFile.name}`);
      const songTask = uploadBytesResumable(songRef, asset.songFile);
      await new Promise((res, rej) =>
        songTask.on('state_changed', () => {}, err => rej(err), () => res())
      );
      const songURLRemote = await getDownloadURL(songTask.snapshot.ref);

      const toMaps = arr => (Array.isArray(arr) ? arr.map(c => ({ id: c.id, tag: c.tag })) : []);

      // Step 3: build payload
      const songPayload = {
        ArtistName:       creatorName,
        ArtistNameLower:  artistLower,
        artistId,
        SongName:         asset.songName,
        isNFT: true,
        songNameLower:    (asset.songName || '').toLowerCase(),
        ArtworkURL:       artworkURLRemote,
        SongURL:          songURLRemote,
        genres:           [asset.genre],
        artistFeatures:   toMaps(asset.credits?.features),
        producers:        toMaps(asset.credits?.producers),
        songwriters:      toMaps(asset.credits?.songwriters),
        recordLabels:     toMaps(asset.credits?.recordLabels),
        collaborators:    [],
        tier:             String(asset.tier || 'Bronze')
      };

      // Step 4: invoke original CF
      const releaseDAT = httpsCallable(functions, 'releaseDAT');
      await releaseDAT({ songs: [songPayload] });

      alert('🚀 DAT released successfully!');
      navigate('/');

    } catch (err) {
      console.error('Release failed:', err);
      alert(`Error: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  // unify credits from form flow or summary map
  const credits = isFromSummary
    ? {
        producers:    sourceMap?.producers       || [],
        features:     sourceMap?.artistFeatures  || [],
        songwriters:  sourceMap?.songwriters     || [],
        recordLabels: sourceMap?.recordLabels    || []
      }
    : (asset.credits || {});

  /* ─── JSX ─── */
  return (
    <div className="detail-page">
      <div className="artwork-container">
        {artworkURL ? (
          <img src={artworkURL} alt="DAT artwork" className="artwork-image pulsating" />
        ) : (
          <div className="no-artwork">No artwork selected</div>
        )}
      </div>

      <div className="creator-section">
        <img
          src={
            profilePreviewURL ||
            artistInfo?.profileImageUrl ||
            sourceMap?.profileImageUrl ||
            artworkURL ||
            ''
          }
          alt="Creator"
          className="profile-image"
        />
        <span className="artist-name">{artistDisplay}</span>
      </div>

      <p><strong>DAT:</strong> {isFromSummary ? (sourceMap?.SongName || sourceMap?.albumName || sourceMap?.name || 'N/A') : (asset.songName || 'N/A')}</p>
      <p><strong>Artist:</strong> {artistDisplay}</p>
      <p><strong>Tier:</strong> {isFromSummary ? (asset.tier || asset.searchTier || 'N/A') : (asset.tier || 'N/A')}</p>
      <p><strong>Genre:</strong> {isFromSummary ? ((Array.isArray(sourceMap?.genres) && sourceMap.genres[0]) || 'N/A') : (asset.genre || 'N/A')}</p>

      <div className="bottom-section">
        {/* credits grid (producers, features, songwriters, labels) */}
        <div className="song-credits-grid">
          {credits.producers?.length > 0 && (
            <CreditList title="Producers" items={credits.producers} />
          )}
          {credits.features?.length > 0 && (
            <CreditList title="Features" items={credits.features} />
          )}
          {credits.songwriters?.length > 0 && (
            <CreditList title="Songwriters" items={credits.songwriters} />
          )}
          {credits.recordLabels?.length > 0 && (
            <CreditList title="Record Labels" items={credits.recordLabels} />
          )}
        </div>
      </div>

      <div className="submit-row">
        <button
          onClick={handleFinalSubmit}
          className="submit-button"
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>

      {/* Full existing CSS */}
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body, html {
          font-family: sans-serif;
          background: #ffffff;
          color: #000;
        }
        .detail-page {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          animation: fadeInPage 0.8s ease-in-out;
        }
        .creator-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        .profile-image {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          margin-bottom: 0.75rem;
        }
        .artist-name {
          font-size: 1.25rem;
          font-weight: 600;
          line-height: 1.2;
          text-align: center;
        }
        @keyframes fadeInPage {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .artwork-container {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        .no-artwork {
          width: 100%;
          height: 200px;
          border: 2px dashed #ccc;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #777;
          font-style: italic;
        }
        .artwork-image {
          width: 100%;
          max-width: 400px;
          pointer-events: none;
          height: auto;
          border-radius: 8px;
          transition: opacity 0.5s ease-in-out;
        }
        .pulsating {
          animation: softPulse 1s ease-out infinite;
          will-change: transform;
        }
        @keyframes softPulse {
          0%   { transform: scale(0.96); }
          50%  { transform: scale(1);    }
          100% { transform: scale(0.96); }
        }
        .details {
          margin-bottom: 1.5rem;
          line-height: 1.4;
        }
        .details p {
          margin-bottom: 0.6rem;
        }
        .bottom-section {
          width: 100%;
          margin-bottom: 2rem;
        }
        .song-credits-grid {
          width: 100%;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }
        .credit-category {
          text-align: left;
        }
        .credit-category h4 {
          margin-bottom: 0.25rem;
          font-size: 1rem;
        }
        .credit-category ul {
          list-style: none;
          padding: 0;
        }
        .credit-category li {
          margin-bottom: 0.25rem;
        }
        .credit-link {
          color: #555;
        }
        .submit-row {
          text-align: center;
        }
        .submit-button {
          padding: 0.75rem 2rem;
          background: #62fbfa;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
        }
        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .submit-button:hover:enabled {
          background: #4fd3d8;
        }
        @media (max-width: 600px) {
          .detail-page {
            padding: 1rem;
          }
          .artwork-image {
            max-width: 100%;
            border-radius: 6px;
          }
        }
      `}</style>
    </div>
  );
};

/* simple stateless list */
const CreditList = ({ title, items }) => (
  <div className="credit-category">
    <h4>{title}</h4>
    <ul>
      {items.map((item, i) => (
        <li key={`${title}-${i}`}>
          <span className="credit-link">{item.tag}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default ViewDatSubmission;
